import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MetricGauge from './MetricGauge';  
import '../styles/StockCard.css';

const CURRENCY_SYMBOLS = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥'
};

const StockCard = ({ 
    stock, 
    onAction, 
    actionIcon, 
    actionType = "default", 
    isPortfolio = false,
    sortBy,
    showActions = true
    }) => {
        const [activeMetricsTab, setActiveMetricsTab] = useState('valuation');
        const [isExpanded, setIsExpanded] = useState(() => {
            if (window.innerWidth < 768) return false;
            return true;
        });
    
    const calculateGrowth = () => {
        if (!stock.current_price || !stock.average_buy_price) return 0;
        return ((stock.current_price - stock.average_buy_price) / stock.average_buy_price) * 100;
    };

    const formatNumber = (num, decimals = 2) => {
        if (num === null || num === undefined) return 'N/A';
        return Number(num).toLocaleString('de-DE', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    const formatCurrencyValue = (value, currency) => {
        if (value === null || value === undefined) return 'N/A';
        const symbol = CURRENCY_SYMBOLS[currency] || currency;
        return `${symbol}${formatNumber(value)}`;
    };

    // const formatBillion = (num) => {
    //     if (num === null || num === undefined) return 'N/A';
    //     return `${(Number(num) / 1000000000).toFixed(2)}B`;
    // };

    const formatBillionWithCurrency = (num, currency) => {
        if (num === null || num === undefined) return 'N/A';
        const symbol = CURRENCY_SYMBOLS[currency] || currency;
        return `${symbol}${(Number(num) / 1000000000).toFixed(2)}B`;
    };

    const periods = [
        { key: 'daily_change', label: '1T' },
        { key: 'weekly_change', label: '1W' },
        { key: 'monthly_change', label: '1M' },
        { key: 'yearly_change', label: '1Y' }
    ];

    const getColorIntensity = (value) => {
        if (!value) return 'var(--card-bg)';
        const absValue = Math.abs(value);
        const intensity = Math.min(Math.max(absValue * 5, 20), 100);
        return value >= 0 
            ? `rgba(16, 185, 129, ${intensity / 100})`  
            : `rgba(239, 68, 68, ${intensity / 100})`;
    };

    const getDisplayValue = () => {
        switch(sortBy) {
            case 'total_growth':
                return `${formatNumber(stock.growth)}%`;
            case 'daily_change':
                return `${formatNumber(stock.daily_change)}%`;
            case 'weekly_change':
                return `${formatNumber(stock.weekly_change)}%`;
            case 'monthly_change':
                return `${formatNumber(stock.monthly_change)}%`;
            case 'yearly_change':
                return `${formatNumber(stock.yearly_change)}%`;
            case 'value':
                return `${formatCurrencyValue(stock.total_value, stock.display_currency)}`;
            case 'dividend':
                return `${formatCurrencyValue(stock.dividend, stock.display_currency)}`;
            case 'dividend_yield':
                return `${formatNumber(stock.dividend_yield)}%`;
            case 'trailing_p_e':
                return formatNumber(stock.trailing_p_e);
            case 'forward_p_e':
                return formatNumber(stock.forward_p_e);
            case 'price_to_book':
                return formatNumber(stock.price_to_book);
            case 'profit_margin':
                return `${formatNumber(stock.profit_margin * 100)}%`;
            case 'revenue':
                return formatBillionWithCurrency(stock.revenue, stock.display_currency);
            case 'market_cap':
                return formatBillionWithCurrency(stock.market_cap, stock.display_currency);
            case 'next_year_growth':
                return `${formatNumber(stock.growth_estimates?.next_year * 100)}%`;
            case 'revenue_growth':
                return `${formatNumber(stock.revenue_growth *100 )}%`;
            case 'earnings_growth':
                return `${formatNumber(stock.earnings_growth *100 )}%`;
            case 'current_price':
                return formatCurrencyValue(stock.current_price, stock.display_currency);
            default:
                return formatCurrencyValue(stock.current_price, stock.display_currency);
        }
    };
    
    const getSortLabel = () => {
        switch(sortBy) {
            case 'total_growth':
                return 'Gesamtwachstum';
            case 'daily_change':
                return '24h Änderung';
            case 'weekly_change':
                return '7d Änderung';
            case 'monthly_change':
                return '30d Änderung';
            case 'yearly_change':
                return '1J Änderung';
            case 'value':
                return 'Gesamtwert';
            case 'dividend':
                return 'Dividende';
            case 'dividend_yield':
                return 'Dividendenrendite';
            case 'trailing_p_e':
                return 'KGV';
            case 'price_to_book':
                return "KBV";
            case 'forward_p_e':
                return 'KGV (erw.)';
            case 'profit_margin':
                return 'Gewinnmarge';
            case 'revenue':
                return 'Umsatz';
            case 'market_cap':
                return 'Marktkapitalisierung';
            case 'next_year_growth':
                return 'Erw. Wachstum (Jahr)';
            case 'revenue_growth':
                return 'Umsatzwachstum';
            case 'earnings_growth':
                return 'Gewinnwachstum';
            case 'current_price':
                return 'Aktueller Preis';
            default:
                return 'Aktueller Kurs';
        }
    };

    const calculateCategoryScores = () => {
        // Calculate average scores for each category
        const valuationScore = Math.round(
          (stock.metricScores?.trailing_p_e?.score || 0) +
          (stock.metricScores?.forward_p_e?.score || 0) +
          (stock.metricScores?.price_to_book?.score || 0) +
          (stock.metricScores?.enterprise_to_ebitda?.score || 0)
        ) / 4;
        
        const financialScore = Math.round(
          (stock.metricScores?.operating_margin?.score || 0) +
          (stock.metricScores?.profit_margin?.score || 0) +
          (stock.metricScores?.gross_margin?.score || 0) +
          (stock.metricScores?.return_on_equity?.score || 0)
        ) / 4;
        
        const dividendScore = Math.round(
          (stock.metricScores?.dividend?.score || 0) +
          (stock.metricScores?.dividend_yield?.score || 0) +
          (stock.metricScores?.payout_ratio?.score || 0)
        ) / 3;
        
        const growthScore = Math.round(
          (stock.metricScores?.revenue_growth?.score || 0) +
          (stock.metricScores?.earnings_growth?.score || 0) +
          (stock.metricScores?.next_year_growth?.score || 0) +
          (stock.metricScores?.current_year_growth?.score || 0)
        ) / 4;
      
        return {
          valuationScore,
          financialScore, 
          dividendScore,
          growthScore
        };
    };

    // Now that we've defined calculateCategoryScores, we can safely use it
    const categoryScores = calculateCategoryScores();
      
    const renderPeriodGrid = () => (
        <div className="card-section performance-section">
            <h3 className="card-section-title">Performance Übersicht</h3>
            <div className="metrics-grid">
                {periods.map(period => {
                    const value = stock[period.key] || 0;
                    return (
                        <div key={period.key} className="metric">
                            <label>{period.label}</label>
                            <div className="metric-value-row">
                                <span className={value >= 0 ? 'positive' : 'negative'}>
                                    <i className={`fas fa-${value >= 0 ? 'arrow-up' : 'arrow-down'}`}></i>
                                    {' '}{Math.abs(value).toFixed(2)}%
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );


    // Replace both renderValuationMetrics and renderFinancialHealth functions with this new combined function:
    const renderCombinedMetrics = () => (
        <div className="card-section">
            <h3 className="card-section-title">Kennzahlen</h3>
            <div className="metrics-tabs">
                <button 
                    className={`tab-button ${activeMetricsTab === 'valuation' ? 'active' : ''}`}
                    onClick={() => setActiveMetricsTab('valuation')}
                >
                    Bewertung
                </button>
                <button 
                    className={`tab-button ${activeMetricsTab === 'financial' ? 'active' : ''}`}
                    onClick={() => setActiveMetricsTab('financial')}
                >
                    Finanzen
                </button>
            </div>
            
            <div className="metrics-content">
                {activeMetricsTab === 'valuation' ? (
                    <div className="metrics-grid">
                        <div className="metric">
                            <label>KGV</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.trailing_p_e)}</span>
                                <MetricGauge score={stock.metricScores?.trailing_p_e?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>KGV (erwartet)</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.forward_p_e)}</span>
                                <MetricGauge score={stock.metricScores?.forward_p_e?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>KBV</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.price_to_book)}</span>
                                <MetricGauge score={stock.metricScores?.price_to_book?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Wert/EBITDA</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.enterprise_to_ebitda)}</span>
                                <MetricGauge score={stock.metricScores?.enterprise_to_ebitda?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Marktkapitalisierung</label>
                            <div className="metric-value-row">
                                <span>{formatBillionWithCurrency(stock.market_cap, stock.display_currency)}</span>
                                <MetricGauge score={stock.metricScores?.market_cap?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Unternehmenswert</label>
                            <div className="metric-value-row">
                                <span>{formatBillionWithCurrency(stock.enterprise_value, stock.display_currency)}</span>
                                <MetricGauge score={stock.metricScores?.enterprise_value?.score || 0} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="metrics-grid">
                        <div className="metric">
                            <label>Umsatz</label>
                            <div className="metric-value-row">
                                <span>{formatBillionWithCurrency(stock.revenue, stock.display_currency)}</span>
                                <MetricGauge score={stock.metricScores?.revenue?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Operative Marge</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.operating_margin * 100)}%</span>
                                <MetricGauge score={stock.metricScores?.operating_margin?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Gewinnmarge</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.profit_margin * 100)}%</span>
                                <MetricGauge score={stock.metricScores?.profit_margin?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Bruttomarge</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.gross_margin * 100)}%</span>
                                <MetricGauge score={stock.metricScores?.gross_margin?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Eigenkapitalrendite</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.return_on_equity * 100)}%</span>
                                <MetricGauge score={stock.metricScores?.return_on_equity?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Free Cashflow</label>
                            <div className="metric-value-row">
                                <span>{formatBillionWithCurrency(stock.free_cash_flow, stock.display_currency)}</span>
                                <MetricGauge score={stock.metricScores?.free_cash_flow?.score || 0} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
    

    const renderDividendSection = () => (
        <div className="card-section dividend-section">
            <h3 className="card-section-title">Dividenden</h3>
            <div className="metrics-grid">
                <div className="metric">
                    <label>Dividende</label>
                    <div className="metric-value-row">
                        <span>{formatCurrencyValue(stock.dividend, stock.display_currency)}</span>
                        <MetricGauge score={stock.metricScores?.dividend?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Dividendenrendite</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.dividend_yield)}%</span>
                        <MetricGauge score={stock.metricScores?.dividend_yield?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Ausschüttungsquote</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.payout_ratio)}%</span>
                        <MetricGauge score={stock.metricScores?.payout_ratio?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Letzte Ausschüttung</label>
                    <div className="metric-value-row">
                        <span>{stock.last_dividend_date ? new Date(stock.last_dividend_date).toLocaleDateString('de-DE') : 'N/A'}</span>
                    </div>
                </div>
            </div>
        </div>
    );
    
    const renderGrowthEstimates = () => (
        <div className="card-section growth-estimates">
            <h3 className="card-section-title">Wachstumsprognosen</h3>
            <div className="metrics-grid">
                <div className="metric">
                    <label>Aktuelles Quartal</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.growth_estimates?.current_qtr * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.current_qtr_growth?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Nächstes Quartal</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.growth_estimates?.next_qtr * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.next_qtr_growth?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Aktuelles Jahr</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.growth_estimates?.current_year * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.current_year_growth?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Nächstes Jahr</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.growth_estimates?.next_year * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.next_year_growth?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Umsatzwachstum</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.revenue_growth * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.revenue_growth?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Gewinnwachstum</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.earnings_growth * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.earnings_growth?.score || 0} />
                    </div>
                </div>
            </div>
        </div>
    );
    
    const renderPortfolioMetrics = () => (
        <div className="card-section portfolio-section">
            <h3 className="card-section-title">Portfolio Details</h3>
            <div className="metrics-grid">
                <div className="metric">
                    <label>Stückzahl</label>
                    <div className="metric-value-row">
                        <span>{stock.quantity}</span>
                    </div>
                </div>
                <div className="metric">
                    <label>Durchschn. Kaufpreis</label>
                    <div className="metric-value-row">
                        <span>{CURRENCY_SYMBOLS[stock.display_currency] || stock.display_currency}{formatNumber(stock.average_buy_price)}</span>
                    </div>
                </div>
                <div className="metric">
                    <label>Aktueller Preis</label>
                    <div className="metric-value-row">
                        <span>{CURRENCY_SYMBOLS[stock.display_currency] || stock.display_currency}{formatNumber(stock.current_price)}</span>
                    </div>
                </div>
                <div className="metric">
                    <label>Entwicklung seit Kauf</label>
                    <div className="metric-value-row">
                        <span className={calculateGrowth() >= 0 ? 'positive' : 'negative'}>{formatNumber(calculateGrowth())}%</span>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="stock-card">
            <div className="stock-card-header">
                <div className="stock-card-title">
                    <Link to={`/stock-detail/${stock.id}`} className="stock-title-link">
                        <h2>{stock.name}</h2>
                    </Link>
                </div>
                <div className="stock-card-actions">
                    <div className="value-container">
                        <span className="stock-value">
                            {getDisplayValue()}
                        </span>
                        {sortBy && <span className="sort-label">{getSortLabel()}</span>}
                    </div>
                    <button 
                        className="expand-button"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} />
                    </button>
                </div>
            </div>
            <div className="stock-card-summary">
                {stock.metricScores && (
                    <div className="summary-grid">
                    <div className="summary-item">
                        <div className="summary-gauge">
                        <MetricGauge score={categoryScores.valuationScore} summaryGauge={true} />
                        </div>
                        <label>Bewertung</label>
                    </div>
                    <div className="summary-item">
                        <div className="summary-gauge">
                        <MetricGauge score={categoryScores.financialScore} summaryGauge={true}/>
                        </div>
                        <label>Finanzen</label>
                    </div>
                    <div className="summary-item">
                        <div className="summary-gauge">
                        <MetricGauge score={categoryScores.dividendScore} summaryGauge={true}/>
                        </div>
                        <label>Dividenden</label>
                    </div>
                    <div className="summary-item">
                        <div className="summary-gauge">
                        <MetricGauge score={categoryScores.growthScore} summaryGauge={true}/>
                        </div>
                        <label>Wachstum</label>
                    </div>
                    </div>
                )}
            </div>
            
            {(isExpanded) && (
                <div className="stock-card-content">
                    {renderPeriodGrid()}
                    {isPortfolio && renderPortfolioMetrics()}
                    {renderCombinedMetrics()}
                    {renderDividendSection()}
                    {renderGrowthEstimates()}

                    {showActions && onAction && (
                        <div className="stock-actions">
                            {isPortfolio ? (
                                <button 
                                    onClick={() => onAction(stock.id)} 
                                    className={`action-button ${actionType}`}
                                >
                                    <i className={`fas ${actionIcon}`}></i>
                                </button>
                            ) : (
                                <div className="search-actions">
                                    {/* <a href={`/stock/${stock.id}`} className="details-button">
                                        <i className="fas fa-chart-line"></i>
                                        Details
                                    </a> */}
                                    {/* <button onClick={() => onAction(stock.id)} className="watchlist-button">
                                        <i className="fas fa-plus"></i>
                                    </button> */}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default StockCard;